import { Component, OnInit } from '@angular/core';
import { CampaignSettingsAbstractComponent } from '../campaign-settings-abstract.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AlertService,
  CampaignService,
  ColorSwatchService,
  LoaderService,
  SideNavService,
  StyleService,
} from '../../_services';
import { FormBuilder } from '@angular/forms';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { BrandColorLabels, Campaign, CampaignSection, Image, Style, StyleLanguage } from '../../_models';
import { DEFAULT_THEME_FONT_FAMILY, fontFamilies } from '../../web-builder/editorConfig';
import { forkJoin } from 'rxjs';

@Component({
  templateUrl: './campaign-branding.component.html',
  styleUrls: ['../campaign-section.scss', './campaign-branding.component.scss'],
})
export class CampaignBrandingComponent extends CampaignSettingsAbstractComponent implements OnInit {
  public section = CampaignSection.Branding;
  public logoFields = ['logo', 'mobile_logo', 'favicon', 'processing_gif', 'chatbot_icon'];
  private fieldLabels = {
    logo: 'Desktop Logo',
    mobile_logo: 'Mobile Logo',
    favicon: 'Favicon',
    processing_gif: 'Processing GIF',
    chatbot_icon: 'Chatbot Icon',
    ...BrandColorLabels,
  };
  private logoFieldDescriptions = {
    logo: 'Your primary logo used across customer-facing solvpath experiences.',
    mobile_logo: 'Displayed on mobile devices.',
    favicon: 'Shows in the browser tab.',
    processing_gif: 'Displays during loading or while waiting',
    chatbot_icon: "Your bot's face! Appears in chat.",
  };
  private logoFieldRules = {
    logo: 'Supports PNG, JPEG, GIF, WebP. Min width: 512px.',
    mobile_logo: 'Supports PNG, JPEG, GIF, WebP.',
    favicon: 'Upload an ICO or PNG file.',
    processing_gif: 'GIF only.',
    chatbot_icon: 'Supports PNG, JPEG, GIF, WebP.',
  }
  private logoFiles = {};
  public selectedField: string = null;
  public themeFields = Object.keys(BrandColorLabels);
  public styleId: string | number = null;
  public defaultFonts = ['Arial', 'Open Sans', 'Ubuntu', 'Custom'];
  public fontOptions = [];
  public showFontDropdown: boolean = false;
  public styleVariables: object = null;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected modalService: NgxSmartModalService,
    protected sideNav: SideNavService,
    protected styleService: StyleService,
    protected loaderService: LoaderService,
    private colorSwatchService: ColorSwatchService
  ) {
    super(router, location, route, campaignService, sideNav, alertService, loaderService);
  }

  ngOnInit() {
    let formControls = { font_family: [DEFAULT_THEME_FONT_FAMILY, null] };
    this.logoFields.forEach((field) => {
      formControls[field] = [null, null];
    });
    this.themeFields.forEach((field) => {
      formControls[field] = [null, null];
    });
    this.form = this.formBuilder.group(formControls);
    super.ngOnInit();

    fontFamilies.forEach((item) => {
      if (!this.defaultFonts.includes(item.name)) {
        this.fontOptions.push({ name: item.name, value: item.value });
      }
    });
  }

  onCampaignFetch(data: Campaign) {
    let patchValues = {};
    this.logoFields.forEach((field) => {
      let fieldValue = data[field];
      patchValues[field] = fieldValue ? fieldValue.id : null;
      let file = fieldValue ? fieldValue.file : null;

      if (file && file !== null && typeof file !== 'string') {
        file = file['small'];
      }
      this.logoFiles[field] = file;
    });
    this.form.patchValue(patchValues);
    this.populateStyles(data);
  }

  populateStyles(campaign: Campaign) {
    let styles = campaign.styles;
    let data: any = {
      campaign: campaign.id,
      name: `Style for ${campaign.name} (${campaign.id})`,
      language: StyleLanguage.CSS,
    };
    let apiCall = this.styleService.create(data);

    if (styles && styles.length > 0) {
      apiCall = this.styleService.get(styles[0]);
    }
    this.loaderService.show();
    apiCall.subscribe(
      (style: Style) => {
        if (style) {
          this.loaderService.hide();
          this.styleId = style.id;
          this.campaign.styles = [style.id as number];
          this.campaignService.currentCampaignUpdated(this.campaign);
          if (style.variables) {
            this.form.patchValue(style.variables);
            this.styleVariables = style.variables;

            let font_family = style.variables['font_family'];
            if (font_family) {
              let valueFamily = fontFamilies.filter((item) => item.value === font_family);
              this.showFontDropdown = valueFamily.length > 0 && !this.defaultFonts.includes(valueFamily[0].name);
            }
          }
        }
      },
      (error) => {
        this.loaderService.hide();
        this.handleError(error);
      }
    );
  }

  getFieldLabel(field: string): string {
    return this.fieldLabels[field] || 'Unknown Field';
  }

  getLogoFieldDescription(field: string): string {
    return this.logoFieldDescriptions[field] || 'No description available.';
  }

  getLogoFieldRules(field: string): string {
    return this.logoFieldRules[field] || '';
  }

  logoFileExists(field: string): boolean {
    return this.logoFiles[field] !== null && this.logoFiles[field] !== undefined;
  }

  getLogoFileUrl(field: string): string {
    return this.logoFileExists(field) ? this.logoFiles[field] : '';
  }

  shouldShowDefaultGifNote(field: string): boolean {
    return field === 'processing_gif' && !this.logoFileExists(field)
  }

  removeImage(field: string) {
    this.logoFiles[field] = null;
    this.form.get(field).setValue(null);
  }

  openImageGallery(field: string) {
    this.selectedField = field;
    this.modalService.getModal('imageGalleryModal').open();
  }

  onImageSelected(image: Image) {
    if (this.selectedField) {
      let file = image.file;

      if (file !== null && typeof file !== 'string') {
        file = file['small'];
      }
      this.logoFiles[this.selectedField] = file;
      this.form.get(this.selectedField).setValue(image.id);
      this.modalService.getModal('imageGalleryModal').close();
      this.selectedField = null;
    }
  }

  closeImageGallery() {
    if (this.selectedField) {
      this.modalService.getModal('imageGalleryModal').close();
      this.selectedField = null;
    }
  }

  getThemeFieldValue(field: string): string {
    const value = this.form.get(field).value;
    return value ? value : '#ffffff';
  }

  onThemeFieldChange(field: string, value: string) {
    if (this.form.get(field).value !== value) {
      this.form.get(field).setValue(value);
    }
  }

  onDefaultFontSelected(font: string) {
    let fontFamily: any = fontFamilies.filter((item) => item.name === font);

    if (fontFamily.length > 0) {
      fontFamily = fontFamily[0];
      this.showFontDropdown = false;
    } else {
      fontFamily = this.fontOptions[0];
      this.showFontDropdown = true;
    }

    this.form.get('font_family').setValue(fontFamily.value);
  }

  onFontChanged(event) {
    let value = event.target.value;
    this.form.get('font_family').setValue(value);
  }

  isFontFamilySelected(font: string) {
    let value = this.form.get('font_family').value;
    let family = this.fontOptions.filter((item) => item.value === value);

    // if custom family is selected and font is custom then return true
    if (family.length > 0 && font === 'Custom') {
      return true;
    }

    let fontFamily = fontFamilies.filter((item) => item.name === font);
    return fontFamily.length > 0 && fontFamily[0].value === value;
  }

  isFontValueSelected(value: string) {
    return this.form.get('font_family').value === value;
  }

  getFontStyle(font: string) {
    let fontFamily = fontFamilies.filter((item) => item.name === font);
    if (fontFamily.length > 0) {
      return fontFamily[0].value;
    }
    return 'inherit';
  }

  protected getFormData(): any {
    let formData = {};
    let values = this.form.value;

    let campaignData = {};
    this.logoFields.forEach((field) => {
      campaignData[field] = values[field];
    });
    formData['campaign'] = campaignData;

    // Initialize styleVariables if it's null
    if (!this.styleVariables) {
      this.styleVariables = {};
    }

    this.themeFields.forEach((field) => {
      if (values[field]) {
        this.styleVariables[field] = values[field];
      }
    });
    this.styleVariables['font_family'] = values['font_family'];
    formData['theme'] = this.styleVariables;

    return formData;
  }

  onSubmit() {
    if (this.form.valid) {
      this.loaderService.show();

      let formData = this.getFormData();
      let apiCalls = [
        this.campaignService.patch(this.campaign.id, formData['campaign']),
        this.styleService.patch(this.styleId, { variables: formData['theme'] }),
      ];

      forkJoin(apiCalls).subscribe(
        (data: [Campaign, Style]) => {
          this.loaderService.hide();
          this.alertService.success('Branding updated successfully.', true);
          this.colorSwatchService.setBrandColors(this.campaign.id, formData['theme']);
          let campaign = { ...this.campaign };

          this.logoFields.forEach((field) => {
            if (this.logoFileExists(field)) {
              if (campaign[field]) {
                (campaign[field] as Image).file = this.logoFiles[field];
                (campaign[field] as Image).id = data[0][field] as string | number;
              } else {
                campaign[field] = { file: this.logoFiles[field], id: data[0][field] } as Image;
              }
            } else {
              campaign[field] = null;
            }
          });

          this.campaignService.currentCampaignUpdated(campaign);
        },
        (error) => {
          this.loaderService.hide();
          this.handleError(error);
        }
      );
    }
  }
}
